import React, { useRef, useState, useEffect, useMemo } from 'react';
import { SpanItem, Container, Wrapper, OverflowTag, ChipItem, ChipWarpper } from './OverflownContentBlock.styled';

type Item = {
  id: string;
  title: string;
};

interface IOverflownContentProps {
  items: string[];
  chip?: boolean;
}

export const OverflownContentBlock: React.FC<IOverflownContentProps> = ({ items = [], chip = false, ...other }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({});

  const _items: Item[] = items.map(item => ({ id: item, title: item }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const update: Record<string, boolean> = {};
        entries.forEach(entry => {
          const {
            target: { dataset }
          } = entry as IntersectionObserverEntry & { target: HTMLElement };

          if (entry.isIntersecting && dataset.itemid) {
            update[dataset.itemid] = true;
          } else if (dataset.itemid) {
            update[dataset.itemid] = false;
          }
        });

        setVisibleMap(prev => ({ ...prev, ...update }));
      },
      {
        root: containerRef.current,
        // rootMargin: '0px',
        threshold: 1
      }
    );

    Array.from(containerRef.current!.children).forEach(item => {
      const { dataset } = item as HTMLElement;
      if (dataset.itemid) {
        observer.observe(item);
      }
    });
    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  const overflownCount = useMemo(() => {
    const entries = Object.values(visibleMap);
    if (entries.length === 1 && entries[0] === false) {
      return 1;
    }
    return items.length === 1 ? 0 : Object.entries(visibleMap).filter(([_, v]) => !v).length;
  }, [items.length, visibleMap]);

  const lastVisibleItemId = useMemo(() => {
    const visibleItems = Object.entries(visibleMap)
      .filter(([_, v]) => v)
      .map(([id, _]) => id);
    return visibleItems[visibleItems.length - 1];
  }, [visibleMap]);

  return (
    <Wrapper>
      <Container {...other} ref={containerRef}>
        {_items.map((item, index) => {
          const isLastVisible = item.id === lastVisibleItemId;
          return !chip ? (
            <SpanItem
              key={item.id}
              data-itemid={item.id}
              isVisible={visibleMap[item.id]}
              hasOverflows={overflownCount > 0}
              style={isLastVisible ? { paddingRight: '30px' } : {}}
            >
              {item.title}
            </SpanItem>
          ) : (
            <ChipWarpper
              key={item.id}
              isVisible={visibleMap[item.id] || index === 0}
              style={isLastVisible ? { paddingRight: '25px' } : {}}
              data-itemid={item.id}
              hasOverflows={overflownCount > 0}
            >
              <ChipItem label={item.title} />
            </ChipWarpper>
          );
        })}
        {overflownCount > 0 && <OverflowTag label={`+${overflownCount}`} />}
      </Container>
    </Wrapper>
  );
};
